import { Request } from 'api';

export interface BaseballRecruitProfile {
    uuid: string;
    individual_uuid: string | null;
    group_uuid: string;
    organization_uuid: string | null;
    user_uuid: string;
    maxpreps_url: string;
    player_bio: PlayerBio | null;
    scraped_data: ScrapedPlayerData | null;
    last_scraped_at: string | null;
    scrape_status: 'pending' | 'active' | 'failed';
    photo_url: string | null;
    photo_path: string | null;
    college_stats_url: string | null;
    college_scraped_data: CollegeScrapedData | null;
    college_last_scraped_at: string | null;
    summer_league_url: string | null;
    summer_league_data: SummerLeagueData | null;
    summer_league_last_scraped_at: string | null;
    individual?: {
        uuid: string;
        name: string;
        profile_photo: string | null;
    };
    created_at: string;
    updated_at: string;
}

export interface PlayerBio {
    first_name: string;
    last_name: string;
    class_year: string;
    positions: string[];
    jersey: string;
    height: string;
    weight: string;
    school: string;
    city: string;
    state: string;
}

export interface BattingStats {
    GP?: string;
    AVG?: string;
    PA?: string;
    AB?: string;
    R?: string;
    H?: string;
    RBI?: string;
    '2B'?: string;
    '3B'?: string;
    HR?: string;
    BB?: string;
    K?: string;
    HBP?: string;
    SF?: string;
    SAC?: string;
    SB?: string;
    OBP?: string;
    SLG?: string;
    OPS?: string;
    [key: string]: string | undefined;
}

export interface PitchingStats {
    ERA?: string;
    W?: string;
    L?: string;
    APP?: string;
    GS?: string;
    CG?: string;
    SHO?: string;
    SV?: string;
    IP?: string;
    H?: string;
    R?: string;
    ER?: string;
    BB?: string;
    K?: string;
    HBP?: string;
    WHIP?: string;
    WP?: string;
    BK?: string;
    [key: string]: string | undefined;
}

export interface FieldingStats {
    GP?: string;
    FP?: string;
    TC?: string;
    PO?: string;
    A?: string;
    E?: string;
    DP?: string;
    [key: string]: string | undefined;
}

export interface SeasonStats {
    year: string;
    age?: string;
    season: string;
    team_level: string;
    grade: string;
    school_name?: string;
    league_name?: string;
    league_abbr?: string;
    batting: BattingStats | null;
    pitching: PitchingStats | null;
    fielding: FieldingStats | null;
    baserunning: { SB?: string; SBA?: string } | null;
    schedule?: GameLogEntry[];
    game_logs?: GameLogEntry[];
}

export interface GameLogEntry {
    date: string;
    opponent: string;
    home_away?: string;
    result?: string;
    score?: string;
    game_type?: string;
    game_url?: string;
    batting?: BattingStats;
    pitching?: PitchingStats;
}

export interface ScrapedPlayerData {
    scrape_metadata?: {
        scrape_date: string;
        url: string;
        version: string;
    };
    player: PlayerBio;
    seasons_data: SeasonStats[];
    career_totals: {
        batting: BattingStats | null;
        pitching: PitchingStats | null;
        fielding: FieldingStats | null;
        baserunning: { SB?: string; SBA?: string } | null;
    };
    batting_rows?: BattingStats[];
    pitching_rows?: PitchingStats[];
}

export interface CollegeScrapedData {
    scrape_metadata?: {
        scrape_date: string;
        stats_url: string;
        team_id: string;
        school_name: string;
        season_year: number | null;
    };
    player: {
        first_name: string;
        last_name: string;
        jersey: string;
        positions: string[];
        class_year: string;
    };
    seasons_data: SeasonStats[];
    career_totals: {
        batting: BattingStats | null;
        pitching: PitchingStats | null;
        fielding: FieldingStats | null;
    };
}

export interface SummerLeagueData {
    scrape_metadata?: {
        scrape_date: string;
        br_url: string;
        br_id: string;
    };
    player: {
        first_name: string;
        last_name: string;
        jersey: string;
        positions: string[];
        class_year: string;
    };
    seasons_data: SeasonStats[];
    career_totals: {
        batting: BattingStats | null;
        pitching: PitchingStats | null;
        fielding: FieldingStats | null;
    };
}

export interface UnmatchedSummerPlayer {
    br_name: string;
    first_name: string;
    last_name: string;
    summer_data: SummerLeagueData;
    suggestions: { profile_uuid: string; name: string; score: number }[];
}

export interface ScrapeSummerStatsResponse {
    success: boolean;
    player_name: string;
    total_seasons: number;
    matched: number;
    unmatched_count: number;
    unmatched_players: UnmatchedSummerPlayer[];
    matched_names: string[];
    message: string;
}

export const getProfiles = (groupUuid: string) => {
    return Request.get<{ profiles: BaseballRecruitProfile[] }>(
        '/workouts/baseball-recruits/profiles',
        { params: { group_uuid: groupUuid } },
    );
};

export const createProfile = (data: {
    group_uuid: string;
    individual_uuid?: string;
    maxpreps_url: string;
}) => {
    return Request.post<{ profile: BaseballRecruitProfile; message: string }>(
        '/workouts/baseball-recruits/profiles',
        data,
    );
};

export const updateProfile = (uuid: string, data: { maxpreps_url?: string }) => {
    return Request.put<{ profile: BaseballRecruitProfile; message: string }>(
        `/workouts/baseball-recruits/profiles/${uuid}`,
        data,
    );
};

export const deleteProfile = (uuid: string) => {
    return Request.delete(`/workouts/baseball-recruits/profiles/${uuid}`);
};

export const scrapeProfile = (profileUuid: string) => {
    return Request.post<{ job_id: string; status: string; message: string }>(
        '/workouts/baseball-recruits/scrape',
        { profile_uuid: profileUuid },
    );
};

export const scrapeAllProfiles = (groupUuid: string) => {
    return Request.post<{ job_ids: string[]; total_queued: number; message: string }>(
        '/workouts/baseball-recruits/scrape-all',
        { group_uuid: groupUuid },
    );
};

export const getScrapeJobStatus = (jobUuid: string) => {
    return Request.get<{ job_id: string; status: string; results: any; error: string | null }>(
        `/workouts/baseball-recruits/jobs/${jobUuid}`,
    );
};

export interface MatchProfileResult {
    profile_uuid: string;
    player_name: string;
    first_name: string;
    last_name: string;
    school: string;
    class_year: string;
    positions: string[];
    member_uuid?: string;
    member_name?: string;
    individual_uuid?: string;
    confidence?: string;
    score?: number;
    potential_members?: {
        member_uuid: string;
        member_name: string;
        individual_uuid: string;
        confidence: string;
        score: number;
    }[];
    potential_individuals?: {
        individual_uuid: string;
        individual_name: string;
        confidence: string;
        score: number;
    }[];
}

export interface MatchProfilesResponse {
    matched: MatchProfileResult[];
    individual_found: MatchProfileResult[];
    unmatched: MatchProfileResult[];
}

export interface ConfirmMatchItem {
    profile_uuid: string;
    individual_uuid?: string;
    create_individual?: {
        first_name: string;
        last_name: string;
    };
}

export const matchProfiles = (groupUuid: string) => {
    return Request.post<MatchProfilesResponse>('/workouts/baseball-recruits/match-profiles', {
        group_uuid: groupUuid,
    });
};

export const confirmMatches = (groupUuid: string, matches: ConfirmMatchItem[]) => {
    return Request.post<{ success: boolean; linked: number; created: number; message: string }>(
        '/workouts/baseball-recruits/confirm-matches',
        { group_uuid: groupUuid, matches },
    );
};

export const unlinkProfile = (profileUuid: string) => {
    return Request.post<{ success: boolean; message: string }>(
        `/workouts/baseball-recruits/profiles/${profileUuid}/unlink`,
    );
};

export const scrapeRoster = (groupUuid: string, rosterUrl: string) => {
    return Request.post<{
        success: boolean;
        players_found: number;
        profiles_created: number;
        scrape_jobs_dispatched: number;
        dispatched_profile_uuids?: string[];
        message: string;
    }>('/workouts/baseball-recruits/scrape-roster', {
        group_uuid: groupUuid,
        roster_url: rosterUrl,
    });
};

export interface PgCollege {
    id: string;
    name: string;
}

export const getPgColleges = () => {
    return Request.get<{ success: boolean; colleges: PgCollege[] }>(
        '/workouts/baseball-recruits/pg-colleges',
    );
};

export const importPgCommitments = (groupUuid: string, collegeName: string, year: number) => {
    return Request.post<{
        success: boolean;
        players_found: number;
        matched_count: number;
        profiles_created: number;
        scrape_jobs_dispatched: number;
        unmatched_players: { name: string; position: string; high_school: string; state: string }[];
        college_logo_url: string;
        message: string;
    }>('/workouts/baseball-recruits/import-pg-commitments', {
        group_uuid: groupUuid,
        college_name: collegeName,
        year,
    });
};

export const setGroupLogo = (groupUuid: string, logoUrl: string) => {
    return Request.post<{ success: boolean; message: string }>(
        '/workouts/baseball-recruits/set-group-logo',
        { group_uuid: groupUuid, logo_url: logoUrl },
    );
};

export interface CollegeRosterPlayer {
    first_name: string;
    last_name: string;
    name: string;
    position: string;
    jersey: string;
    height: string;
    weight: string;
    year_class: string;
    hometown: string;
    high_school: string;
    state: string;
    photo_url: string;
}

export interface CollegeRosterMatch {
    player: CollegeRosterPlayer;
    match: {
        member_uuid: string;
        member_name: string;
        individual_uuid: string | null;
        score: number;
        confidence: string;
    };
    potential_matches: {
        member_uuid: string;
        member_name: string;
        individual_uuid: string | null;
        score: number;
        confidence: string;
    }[];
}

export interface CollegeRosterUnmatched {
    player: CollegeRosterPlayer;
    potential_matches: {
        member_uuid: string;
        member_name: string;
        individual_uuid: string | null;
        score: number;
        confidence: string;
    }[];
}

export interface CollegeRosterImportResponse {
    success: boolean;
    players_found: number;
    matched: CollegeRosterMatch[];
    unmatched: CollegeRosterUnmatched[];
}

export const importCollegeRoster = (groupUuid: string, rosterUrl: string) => {
    return Request.post<CollegeRosterImportResponse>(
        '/workouts/baseball-recruits/import-college-roster',
        { group_uuid: groupUuid, roster_url: rosterUrl },
    );
};

export interface ConfirmCollegeRosterPlayer {
    first_name: string;
    last_name: string;
    action: 'create' | 'link' | 'skip';
    individual_uuid?: string;
    photo_url?: string;
    position?: string;
    jersey?: string;
    hometown?: string;
    high_school?: string;
    home_state?: string;
}

export const confirmCollegeRoster = (groupUuid: string, players: ConfirmCollegeRosterPlayer[]) => {
    return Request.post<{
        success: boolean;
        created: number;
        linked: number;
        skipped: number;
        message: string;
    }>('/workouts/baseball-recruits/confirm-college-roster', {
        group_uuid: groupUuid,
        players,
    });
};

export const scrapeGroupMembers = (groupUuid: string) => {
    return Request.post<{
        success: boolean;
        searched: number;
        matched: number;
        profiles_created: number;
        scrape_jobs_dispatched: number;
        dispatched_profile_uuids: string[];
        message: string;
    }>('/workouts/baseball-recruits/scrape-group-members', { group_uuid: groupUuid });
};

export const searchBrSummerStats = (groupUuid: string) => {
    return Request.post<{ success: boolean; job_id: string; message: string }>(
        '/workouts/baseball-recruits/search-br-summer-stats',
        { group_uuid: groupUuid },
    );
};

export interface UnmatchedCollegePlayer {
    wmt_name: string;
    first_name: string;
    last_name: string;
    jersey: string;
    positions: string[];
    class_year: string;
    college_data: CollegeScrapedData;
    suggestions: { profile_uuid: string; name: string; score: number }[];
}

export interface ScrapeCollegeStatsResponse {
    success: boolean;
    school_name: string;
    season_year: number | null;
    total_players: number;
    matched: number;
    unmatched_count: number;
    unmatched_players: UnmatchedCollegePlayer[];
    matched_names: string[];
    stats_url?: string;
    message: string;
}

export const scrapeCollegeStats = (groupUuid: string, statsUrl: string) => {
    return Request.post<ScrapeCollegeStatsResponse>(
        '/workouts/baseball-recruits/scrape-college-stats',
        { group_uuid: groupUuid, stats_url: statsUrl },
    );
};

export const confirmCollegeStatsMatches = (
    groupUuid: string,
    statsUrl: string,
    matches: { profile_uuid: string; college_data: CollegeScrapedData }[],
) => {
    return Request.post<{ success: boolean; saved: number; message: string }>(
        '/workouts/baseball-recruits/confirm-college-stats-matches',
        { group_uuid: groupUuid, stats_url: statsUrl, matches },
    );
};

export const refreshCollegeStats = (groupUuid: string) => {
    return Request.post<{ success: boolean; job_id: string; message: string }>(
        '/workouts/baseball-recruits/refresh-college-stats',
        { group_uuid: groupUuid },
    );
};

export const scrapeSummerStats = (groupUuid: string, brUrl: string) => {
    return Request.post<ScrapeSummerStatsResponse>(
        '/workouts/baseball-recruits/scrape-summer-stats',
        { group_uuid: groupUuid, br_url: brUrl },
    );
};

export const confirmSummerStatsMatches = (
    groupUuid: string,
    brUrl: string,
    matches: { profile_uuid: string; summer_data: SummerLeagueData }[],
) => {
    return Request.post<{ success: boolean; saved: number; message: string }>(
        '/workouts/baseball-recruits/confirm-summer-stats-matches',
        { group_uuid: groupUuid, br_url: brUrl, matches },
    );
};
